/**
 * @file DataGridEditableCell.tsx
 * @description Inline editable cell for the DataGrid.
 *
 * Behavior:
 * - Double-click to enter edit mode
 * - Displays an input field matching the cell type
 * - Save on blur or Enter key
 * - Cancel on Escape key
 * - Shows validation errors inline
 */

import { useState, useRef, useEffect, useCallback, memo } from 'react';
import { cn } from '@/lib/utils';
import { Input } from '../../input';
import type { DataGridColumn, DataGridCellType } from './dataGridTypes';

interface DataGridEditableCellProps<TRow> {
  value: unknown;
  row: TRow;
  rowIndex: number;
  column: DataGridColumn<TRow>;
  isEditable: boolean;
  onSave: (newValue: unknown) => void;
  formatValue: (value: unknown, type?: DataGridCellType, column?: DataGridColumn<TRow>) => React.ReactNode;
}

function DataGridEditableCellInner<TRow>({
  value,
  row,
  rowIndex,
  column,
  isEditable,
  onSave,
  formatValue,
}: DataGridEditableCellProps<TRow>) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const startEdit = useCallback(() => {
    if (!isEditable) return;
    setEditValue(value == null ? '' : String(value));
    setError(null);
    setIsEditing(true);
  }, [isEditable, value]);

  const cancelEdit = useCallback(() => {
    setIsEditing(false);
    setError(null);
  }, []);

  const commitEdit = useCallback(() => {
    const newValue = editValue.trim();

    // Validate if validator provided
    if (column.validate) {
      const validationError = column.validate(newValue, row);
      if (validationError) {
        setError(validationError);
        return;
      }
    }

    // Only save if value actually changed
    const oldStr = value == null ? '' : String(value);
    if (newValue !== oldStr) {
      onSave(newValue);
    }

    setIsEditing(false);
    setError(null);
  }, [editValue, value, column, row, onSave]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        commitEdit();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        cancelEdit();
      }
    },
    [commitEdit, cancelEdit],
  );

  // Edit mode
  if (isEditing) {
    return (
      <div className="flex items-center gap-1 -my-1">
        <Input
          ref={inputRef}
          value={editValue}
          onChange={(e) => {
            setEditValue(e.target.value);
            setError(null);
          }}
          onBlur={commitEdit}
          onKeyDown={handleKeyDown}
          type={column.type === 'currency' ? 'number' : 'text'}
          step={column.type === 'currency' ? '0.01' : undefined}
          className={cn(
            'h-7 text-[12px] px-2 py-0.5',
            error && 'border-destructive focus:ring-destructive',
          )}
          aria-invalid={!!error}
          aria-describedby={error ? `cell-error-${rowIndex}-${String(column.key)}` : undefined}
        />
        {error && (
          <span
            id={`cell-error-${rowIndex}-${String(column.key)}`}
            className="text-[11px] text-destructive whitespace-nowrap"
          >
            {error}
          </span>
        )}
      </div>
    );
  }

  // Display mode
  return (
    <span
      onDoubleClick={startEdit}
      className={cn(
        'block truncate',
        isEditable && 'cursor-text hover:bg-muted/40 rounded px-1 -mx-1 transition-colors',
      )}
      title={isEditable ? 'Double-click to edit' : undefined}
    >
      {formatValue(value, column.type, column)}
    </span>
  );
}

export const DataGridEditableCell = memo(DataGridEditableCellInner) as typeof DataGridEditableCellInner;
